import type { EmbeddedViewRef, TemplateRef } from '@angular/core';

import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ViewContainerRef,
    afterNextRender,
    computed,
    inject,
    input,
    viewChild,
    DOCUMENT,
} from '@angular/core';
import { animate, style, transition, trigger, state } from '@angular/animations';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';

import { Actions, ofType } from '@ngrx/effects';

import type { Uuid } from '@common/types';
import type { PanelPosition, SelectedEntity } from '@common/types/layout.type';
import { ESelectedEntityType } from '@common/types/layout.type';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import LayoutActions from '@web/store/layout/layout.actions';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';

import { SpClickOutsideDirective } from '@common/directives/click-outside.directive';

import { ThemeSettingsComponent } from '@web/shared/components/web-settings/theme-settings/theme-settings.component';
import { BlockSettingsComponent } from '@web/shared/components/web-settings/block-settings/block-settings.component';
import { SpecificBlockSettingsComponent } from '@web/shared/components/web-settings/specific-block-settings/specific-block-settings.component';
import { SpecificSectionSettingsComponent } from '@web/shared/components/web-settings/specific-section-settings/specific-section-settings.component';
import { ColumnSettingsComponent } from '@web/shared/components/web-settings/column-settings/column-settings.component';
import { GridSettingsComponent } from '@web/shared/components/web-settings/grid-settings/grid-settings.component';
import { TabSettingsComponent } from '@web/shared/components/web-settings/tab-settings/tab-settings.component';
import { WidgetSettingsComponent } from '@web/shared/components/web-settings/widget-settings/widget-settings.component';
import { ZeroBlockSettingsComponent } from '@web/shared/components/web-settings/zero-block-settings/zero-block-settings.component';

/** Where the panel's markup is parked — a placeholder in the app root, outside the editor's transforms. */
const PORTAL_OUTLET_SELECTOR = '#settingsPanelContent';

/** The pulse the panel plays when the selection moves under it — see `.scaled` in the stylesheet. */
const PULSE_CLASS = 'scaled';
const PULSE_TARGET_SELECTOR = '.animation-wrapper';
const PULSE_DURATION_MS = 200;

@Component({
    selector: 'web-settings-panel',
    templateUrl: './settings-panel.component.html',
    styleUrls: ['./settings-panel.component.less'],
    animations: [
        trigger('fade', [
            state('void', style({ opacity: 0 })),
            state('in', style({ opacity: 1 })),
            transition(':enter', [animate('0.15s ease-in-out')]),
            transition(':leave', [animate('0.1s ease-in-out')]),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpClickOutsideDirective,
        ThemeSettingsComponent,
        BlockSettingsComponent,
        ZeroBlockSettingsComponent,
        SpecificBlockSettingsComponent,
        SpecificSectionSettingsComponent,
        ColumnSettingsComponent,
        WidgetSettingsComponent,
        GridSettingsComponent,
        TabSettingsComponent,
    ],
})
export class SettingsPanelComponent {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly blockFacade = inject(BlocksFacade);
    private readonly sectionFacade = inject(SectionsFacade);
    private readonly columnFacade = inject(ColumnsFacade);
    private readonly gridsFacade = inject(GridsFacade);
    private readonly tabsFacade = inject(TabsFacade);
    private readonly widgetFacade = inject(WidgetsFacade);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly actions$ = inject(Actions);

    private readonly settingsPanelPortal = viewChild.required<TemplateRef<unknown>>('settingsPanelPortal');

    public readonly panelPosition = input.required<PanelPosition>();

    /**
     * The selection the panel edits, handed down by the host rather than read from
     * the store again: the host already renders the panel off the same value (it is
     * what `showSettings` gates), so a second subscription could only add a frame of
     * disagreement between the two.
     */
    public readonly selectedEntity = input.required<SelectedEntity>();

    public readonly entityType = computed(() => this.selectedEntity()?.entityType);
    private readonly entityId = computed<Uuid>(() => this.selectedEntity()?.entityId ?? '');

    /**
     * The entity dictionaries, each one app-wide memoized selector read as a signal.
     *
     * The panel resolves the selected entity out of them with `computed` instead of
     * asking a facade for a `<kind>ById$(id)` stream: those are selector *factories*,
     * so every call builds a fresh memoized selector, and the previous shape of this
     * component — getters returning a new observable, read through `async` — rebuilt
     * one on every change detection pass and threw the old one away. Reading a
     * dictionary costs one property lookup and hands back the same entity reference
     * until the entity itself changes, so the settings components below re-render
     * only when their own entity does.
     */
    private readonly blocks = toSignal(this.blockFacade.blocksEntities$, { requireSync: true });
    private readonly sections = toSignal(this.sectionFacade.sectionsEntities$, { requireSync: true });
    private readonly columns = toSignal(this.columnFacade.columnsEntities$, { requireSync: true });
    private readonly grids = toSignal(this.gridsFacade.gridsEntities$, { requireSync: true });
    private readonly tabs = toSignal(this.tabsFacade.tabsEntities$, { requireSync: true });
    private readonly widgets = toSignal(this.widgetFacade.widgetsEntities$, { requireSync: true });

    /**
     * The selection identity, wrapped in a list so the template can `track` it.
     *
     * The panel is attached to its portal once and then reused, so without a key
     * a settings component survives a move to another entity of the same kind:
     * it would keep the form it took on init, show the previous entity's values
     * and write every edit into it. Tracking the selection rebuilds the component
     * when — and only when — it actually moves.
     *
     * The type is part of the key, not just the id: the theme is selected with no
     * id at all (`ESelectedEntityType.theme` alone), so keying on the id would
     * leave the whole panel empty for it.
     */
    public readonly selectionKey = computed<string[]>(() => {
        const entityType = this.entityType();

        return entityType ? [`${entityType}:${this.entityId()}`] : [];
    });

    public readonly panelTransform = computed<string>(() => {
        const { x = 0, y = 0 } = this.panelPosition() ?? {};

        return `translate3d(${x}px, ${y}px, 0)`;
    });

    /**
     * The selected entity per kind. Which one the template reads is decided by the
     * selection type, and the branches for the block subtypes cast through `$any`:
     * the type is set by the component that owns the block, alongside its subtype.
     */
    public readonly selectedBlock = computed(() => this.blocks()[this.entityId()]);
    public readonly selectedSection = computed(() => this.sections()[this.entityId()]);
    public readonly selectedColumn = computed(() => this.columns()[this.entityId()]);
    public readonly selectedGrid = computed(() => this.grids()[this.entityId()]);
    public readonly selectedTab = computed(() => this.tabs()[this.entityId()]);
    public readonly selectedWidget = computed(() => this.widgets()[this.entityId()]);

    /** The column's own section — the column settings edit both sides of that pair. */
    public readonly selectedSectionByColumnId = computed(() => {
        const column = this.selectedColumn();

        return column ? this.sections()[column.parentId] : undefined;
    });

    public readonly ESelectedEntityType = ESelectedEntityType;

    private panelView: EmbeddedViewRef<unknown> | null = null;
    private pulseTarget: HTMLElement | null = null;
    private pulseTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        this.actions$
            .pipe(ofType(LayoutActions.setSelectedEntity), takeUntilDestroyed())
            .subscribe(() => this.playSelectionPulse());

        afterNextRender(() => this.attachToPortal());

        this.destroyRef.onDestroy(() => this.clearPulse());
    }

    public closePanel(): void {
        this.layoutFacade.unsetEntity();
    }

    /**
     * Moves the panel into the app-level outlet.
     *
     * Note the outlet is *shared*: it belongs to the app shell and every panel of
     * this kind is parked in it, so this must never `dispose()` the outlet — CDK's
     * `DomPortalOutlet.dispose` removes the host element itself, and no later panel
     * would have anywhere to attach. Nothing is detached by hand on the way out
     * either: the attached view is a child of this component's view container, so
     * removing the panel removes the nodes it moved into the outlet with it.
     */
    private attachToPortal(): void {
        const outletElement = this.document.querySelector<HTMLElement>(PORTAL_OUTLET_SELECTOR);

        if (!outletElement) return;

        this.panelView = new DomPortalOutlet(outletElement).attach(
            new TemplatePortal(this.settingsPanelPortal(), this.viewContainerRef)
        ) as EmbeddedViewRef<unknown>;
    }

    /**
     * Nudges the panel when the selection moves, so it is visible that the settings
     * on screen now belong to something else.
     *
     * The target is looked up inside the panel's own view — the markup lives in a
     * template, out of reach of a view query, and the panel is in a portal, so
     * there is no host element to walk down from either.
     */
    private playSelectionPulse(): void {
        this.pulseTarget ??= this.findPulseTarget();

        const target = this.pulseTarget;

        if (!target) return;

        this.clearPulse();
        target.classList.add(PULSE_CLASS);

        this.pulseTimeout = setTimeout(() => target.classList.remove(PULSE_CLASS), PULSE_DURATION_MS);
    }

    private findPulseTarget(): HTMLElement | null {
        const rootElement = this.panelView?.rootNodes.find((node): node is HTMLElement => node instanceof HTMLElement);

        return rootElement?.querySelector<HTMLElement>(PULSE_TARGET_SELECTOR) ?? null;
    }

    private clearPulse(): void {
        if (this.pulseTimeout === null) return;

        clearTimeout(this.pulseTimeout);
        this.pulseTimeout = null;
    }
}
